import React from 'react';
import {
  Box, Typography, Paper, Chip, Stack, Tooltip, IconButton,
} from '@mui/material';
import { OpenInNew as OpenIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { STATUS_CONFIG, PRIORITY_COLORS, TIMEZONE_LABELS } from './SalesDashboard';

const PIPELINE_COLUMNS = [
  'new_lead', 'contacted', 'qualified', 'application_sent',
  'application_received', 'under_review', 'approved', 'funded',
  'declined_by_bank', 'client_declined_offer',
];

const currency = (n) => n != null ? `$${Number(n).toLocaleString()}` : null;

export default function PipelineView({ leads, onOpenDetail, onStatusChange }) {
  const byStatus = PIPELINE_COLUMNS.reduce((acc, s) => {
    acc[s] = leads.filter((l) => l.status === s);
    return acc;
  }, {});

  return (
    <Box sx={{ overflowX: 'auto', pb: 2 }}>
      <Stack direction="row" gap={1.5} sx={{ minWidth: PIPELINE_COLUMNS.length * 220 }}>
        {PIPELINE_COLUMNS.map((status) => {
          const cfg = STATUS_CONFIG[status];
          const col = byStatus[status] || [];
          return (
            <Box key={status} sx={{ width: 210, flexShrink: 0 }}>
              {/* Column header */}
              <Box sx={{
                bgcolor: cfg.color,
                color: '#fff',
                px: 1.5,
                py: 0.75,
                borderRadius: '8px 8px 0 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <Typography variant="caption" fontWeight={700} fontSize="0.75rem">{cfg.label}</Typography>
                <Chip label={col.length} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', height: 18, fontSize: '0.7rem' }} />
              </Box>

              {/* Column body */}
              <Box sx={{
                bgcolor: cfg.bg,
                borderRadius: '0 0 8px 8px',
                minHeight: 300,
                maxHeight: '70vh',
                overflowY: 'auto',
                p: 0.75,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}>
                {col.map((lead) => (
                  <PipelineCard
                    key={lead._id}
                    lead={lead}
                    statusCfg={cfg}
                    onOpenDetail={onOpenDetail}
                    onStatusChange={onStatusChange}
                  />
                ))}
                {col.length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center', mt: 2, display: 'block' }}>
                    Empty
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function PipelineCard({ lead, statusCfg, onOpenDetail, onStatusChange }) {
  const phone = lead.mobilePhone || lead.phone || lead.businessPhone;
  return (
    <Paper
      elevation={1}
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        cursor: 'pointer',
        borderLeft: `3px solid ${PRIORITY_COLORS[lead.priority] || '#94a3b8'}`,
        '&:hover': { boxShadow: 4 },
      }}
      onClick={() => onOpenDetail(lead)}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" fontWeight={700} noWrap display="block" title={lead.businessName}>
            {lead.businessName || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {[lead.firstName, lead.lastName].filter(Boolean).join(' ')}
          </Typography>
          {currency(lead.requestedAmount) && (
            <Typography variant="caption" color="success.main" fontWeight={600}>
              {currency(lead.requestedAmount)}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled" display="block">
            {TIMEZONE_LABELS[lead.timezone]?.split(' ')[0]}
          </Typography>
        </Box>
        <Stack>
          {phone && (
            <Tooltip title={`Call ${phone}`}>
              <IconButton
                size="small"
                component="a"
                href={`tel:${phone}`}
                onClick={(e) => e.stopPropagation()}
                sx={{ p: 0.25 }}
              >
                <PhoneIcon sx={{ fontSize: 14, color: 'success.main' }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Open detail">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onOpenDetail(lead); }} sx={{ p: 0.25 }}>
              <OpenIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      {lead.notes?.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {lead.notes[0].content}
        </Typography>
      )}
    </Paper>
  );
}
